import React, { useState, useEffect } from 'react';
import { BookOpen, Plus, Edit2, Trash2, Save, X, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface KnowledgeBaseEntry {
  id: string;
  brand_id: string;
  category: string;
  subcategory: string | null;
  title: string;
  content: string;
  variables: Record<string, any>;
  applicable_services: string[];
  applicable_client_types: string[];
  priority: number;
  is_required: boolean;
  is_active: boolean;
  version: number;
  effective_date: string;
  created_at: string;
  updated_at: string;
}

interface KnowledgeBaseManagementProps {
  brandId: string;
}

const CATEGORIES = [
  { value: 'legal', label: 'Legal' },
  { value: 'service_specific', label: 'Service Specific' },
  { value: 'payment_terms', label: 'Payment Terms' },
  { value: 'deliverables', label: 'Deliverables' },
  { value: 'warranties', label: 'Warranties' },
  { value: 'termination', label: 'Termination' },
  { value: 'confidentiality', label: 'Confidentiality' },
  { value: 'intellectual_property', label: 'Intellectual Property' },
  { value: 'general', label: 'General' },
  { value: 'compliance', label: 'Compliance' },
];

const SERVICE_TYPES = [
  { value: 'web', label: 'Web Development' },
  { value: 'marketing', label: 'Digital Marketing' },
];

const CLIENT_TYPES = [
  { value: 'local', label: 'Local Business' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'b2b', label: 'B2B' },
];

export default function KnowledgeBaseManagement({ brandId }: KnowledgeBaseManagementProps) {
  const [entries, setEntries] = useState<KnowledgeBaseEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingEntry, setEditingEntry] = useState<KnowledgeBaseEntry | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const emptyEntry: Partial<KnowledgeBaseEntry> = {
    brand_id: brandId,
    category: 'legal',
    subcategory: null,
    title: '',
    content: '',
    variables: {},
    applicable_services: [],
    applicable_client_types: [],
    priority: 100,
    is_required: false,
    is_active: true,
    version: 1,
  };

  useEffect(() => {
    fetchEntries();
  }, [brandId]);

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('knowledge_base')
        .select('*')
        .eq('brand_id', brandId)
        .order('category', { ascending: true })
        .order('priority', { ascending: true });

      if (error) throw error;
      setEntries(data || []);
    } catch (error) {
      console.error('Error fetching knowledge base:', error);
      alert('Failed to load knowledge base entries');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editingEntry) return;

    try {
      if (editingEntry.id) {
        const { error } = await supabase
          .from('knowledge_base')
          .update({
            category: editingEntry.category,
            subcategory: editingEntry.subcategory,
            title: editingEntry.title,
            content: editingEntry.content,
            applicable_services: editingEntry.applicable_services,
            applicable_client_types: editingEntry.applicable_client_types,
            priority: editingEntry.priority,
            is_required: editingEntry.is_required,
            is_active: editingEntry.is_active,
          })
          .eq('id', editingEntry.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('knowledge_base')
          .insert({
            brand_id: brandId,
            category: editingEntry.category,
            subcategory: editingEntry.subcategory,
            title: editingEntry.title,
            content: editingEntry.content,
            applicable_services: editingEntry.applicable_services,
            applicable_client_types: editingEntry.applicable_client_types,
            priority: editingEntry.priority,
            is_required: editingEntry.is_required,
            is_active: editingEntry.is_active,
            version: 1,
          });

        if (error) throw error;
      }

      await fetchEntries();
      setEditingEntry(null);
      setIsCreating(false);
    } catch (error) {
      console.error('Error saving entry:', error);
      alert('Failed to save knowledge base entry');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this entry?')) return;

    try {
      const { error } = await supabase
        .from('knowledge_base')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await fetchEntries();
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert('Failed to delete knowledge base entry');
    }
  };

  const filteredEntries = entries.filter(entry => {
    const matchesCategory = filterCategory === 'all' || entry.category === filterCategory;
    const matchesSearch = searchTerm === '' ||
      entry.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.content.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleService = (service: string) => {
    if (!editingEntry) return;
    const services = editingEntry.applicable_services || [];
    const newServices = services.includes(service)
      ? services.filter(s => s !== service)
      : [...services, service];
    setEditingEntry({ ...editingEntry, applicable_services: newServices });
  };

  const toggleClientType = (type: string) => {
    if (!editingEntry) return;
    const types = editingEntry.applicable_client_types || [];
    const newTypes = types.includes(type)
      ? types.filter(t => t !== type)
      : [...types, type];
    setEditingEntry({ ...editingEntry, applicable_client_types: newTypes });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (editingEntry || isCreating) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center space-x-2">
            <BookOpen className="w-5 h-5" />
            <span>{editingEntry?.id ? 'Edit' : 'Create'} Knowledge Base Entry</span>
          </h3>
          <button
            onClick={() => {
              setEditingEntry(null);
              setIsCreating(false);
            }}
            className="p-2 text-gray-600 hover:text-gray-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium">Knowledge Base Content Guidelines:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Write clear, professional legal language</li>
                <li>Use {'{'}variable_name{'}'} for dynamic content (e.g., {'{'}client_name{'}'})</li>
                <li>Be specific about applicable services and client types</li>
                <li>Required entries will always be included in agreements</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category *
              </label>
              <select
                value={editingEntry?.category || ''}
                onChange={(e) => setEditingEntry(editingEntry ? { ...editingEntry, category: e.target.value } : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subcategory
              </label>
              <input
                type="text"
                value={editingEntry?.subcategory || ''}
                onChange={(e) => setEditingEntry(editingEntry ? { ...editingEntry, subcategory: e.target.value || null } : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Optional subcategory"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>
            <input
              type="text"
              value={editingEntry?.title || ''}
              onChange={(e) => setEditingEntry(editingEntry ? { ...editingEntry, title: e.target.value } : null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Payment Schedule Terms"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Content *
            </label>
            <textarea
              value={editingEntry?.content || ''}
              onChange={(e) => setEditingEntry(editingEntry ? { ...editingEntry, content: e.target.value } : null)}
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              placeholder="Enter the clause or content. Use {variable_name} for dynamic values."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Applicable Services
              </label>
              <div className="space-y-2">
                {SERVICE_TYPES.map(service => (
                  <label key={service.value} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={editingEntry?.applicable_services?.includes(service.value) || false}
                      onChange={() => toggleService(service.value)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{service.label}</span>
                  </label>
                ))}
                <p className="text-xs text-gray-500 mt-2">
                  Leave unchecked to apply to all services
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Applicable Client Types
              </label>
              <div className="space-y-2">
                {CLIENT_TYPES.map(type => (
                  <label key={type.value} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={editingEntry?.applicable_client_types?.includes(type.value) || false}
                      onChange={() => toggleClientType(type.value)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{type.label}</span>
                  </label>
                ))}
                <p className="text-xs text-gray-500 mt-2">
                  Leave unchecked to apply to all client types
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <input
                type="number"
                value={editingEntry?.priority || 100}
                onChange={(e) => setEditingEntry(editingEntry ? { ...editingEntry, priority: parseInt(e.target.value) || 100 } : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                min="1"
              />
              <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
            </div>

            <div>
              <label className="flex items-center space-x-2 mt-7">
                <input
                  type="checkbox"
                  checked={editingEntry?.is_required || false}
                  onChange={(e) => setEditingEntry(editingEntry ? { ...editingEntry, is_required: e.target.checked } : null)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Required</span>
              </label>
              <p className="text-xs text-gray-500 mt-1">Always include in agreements</p>
            </div>

            <div>
              <label className="flex items-center space-x-2 mt-7">
                <input
                  type="checkbox"
                  checked={editingEntry?.is_active !== false}
                  onChange={(e) => setEditingEntry(editingEntry ? { ...editingEntry, is_active: e.target.checked } : null)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Active</span>
              </label>
              <p className="text-xs text-gray-500 mt-1">Currently in use</p>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={() => {
                setEditingEntry(null);
                setIsCreating(false);
              }}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!editingEntry?.title || !editingEntry?.content}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <Save className="w-4 h-4" />
              <span>Save Entry</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center space-x-2">
          <BookOpen className="w-5 h-5" />
          <span>Knowledge Base Management</span>
        </h3>
        <button
          onClick={() => {
            setEditingEntry(emptyEntry as KnowledgeBaseEntry);
            setIsCreating(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add Entry</span>
        </button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">About Knowledge Base</p>
            <p className="mt-1">
              The knowledge base stores reusable content that Claude AI uses to generate custom service agreements.
              Entries are automatically selected based on services included and client type.
            </p>
          </div>
        </div>
      </div>

      <div className="flex space-x-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search entries..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Categories</option>
          {CATEGORIES.map(cat => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </select>
      </div>

      <div className="space-y-3">
        {filteredEntries.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p>No knowledge base entries found</p>
            <button
              onClick={() => {
                setEditingEntry(emptyEntry as KnowledgeBaseEntry);
                setIsCreating(true);
              }}
              className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
            >
              Create your first entry
            </button>
          </div>
        ) : (
          filteredEntries.map(entry => (
            <div
              key={entry.id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="font-semibold text-gray-900">{entry.title}</h4>
                    <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                      {CATEGORIES.find(c => c.value === entry.category)?.label}
                    </span>
                    {entry.is_required && (
                      <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">
                        Required
                      </span>
                    )}
                    {!entry.is_active && (
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded">
                        Inactive
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2 mb-2">{entry.content}</p>
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    {entry.applicable_services?.length > 0 && (
                      <span>Services: {entry.applicable_services.join(', ')}</span>
                    )}
                    {entry.applicable_client_types?.length > 0 && (
                      <span>Clients: {entry.applicable_client_types.join(', ')}</span>
                    )}
                    <span>Priority: {entry.priority}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => setEditingEntry(entry)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(entry.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
